import React, { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { Button, SkeletonLoader } from '../../../components'
import { useMessage, useModalDialog } from '../../../hooks'
import { candidateService } from '../services'
import { getMilestone } from '../helpers/milestones'
import { useCandidateDialog } from '../hooks'
import '../index.css'

export const CandidateDetail = () => {
  const { uuid } = useParams()

  const [candidate, setCandidate] = useState(null)
  const [milestones, setMilestones] = useState([])
  const [flags, setFlags] = useState([])
  const [interviews, setInterviews] = useState([])
  const [loading, setLoading] = useState(true)

  const { showMessage } = useMessage()
  const { hideModalDialog } = useModalDialog()
  const {
    showAddMilestoneDialog, showEditMilestoneDialog, showDeleteMilestoneDialog,
    showEditCandidateDialog,
  } = useCandidateDialog()

  const load = () => {
    candidateService.getByUuid(uuid)
      .then(res => {
        setCandidate(res.candidate)
        setMilestones(res.milestones || [])
        setFlags(res.flags || [])
        setInterviews(res.interviews || [])
      })
      .catch(err => showMessage(JSON.stringify(err), 'error'))
      .finally(() => setLoading(false))
  }

  useEffect(() => { load() }, [uuid])

  const addMilestone = async (payload) => {
    await candidateService.addMilestone(uuid, payload)
      .then(() => { hideModalDialog(); load(); showMessage('Händelse tillagd') })
      .catch(err => { showMessage(JSON.stringify(err), 'error'); throw err })
  }

  const editCandidate = () => {
    showEditCandidateDialog([candidate], async (payload) => {
      await candidateService.update(uuid, payload)
        .then(() => { hideModalDialog(); load(); showMessage('Kandidat uppdaterad') })
        .catch(err => { showMessage(JSON.stringify(err), 'error'); throw err })
    })
  }

  const editMilestone = (milestone) => {
    showEditMilestoneDialog(milestone, async (payload) => {
      await candidateService.updateMilestone(uuid, milestone.candidate_milestone_id, payload)
        .then(() => { hideModalDialog(); load(); showMessage('Händelse uppdaterad') })
        .catch(err => { showMessage(JSON.stringify(err), 'error'); throw err })
    })
  }

  const deleteMilestone = (milestone) => {
    showDeleteMilestoneDialog(milestone, async () => {
      await candidateService.removeMilestone(uuid, milestone.candidate_milestone_id)
        .then(() => { hideModalDialog(); load(); showMessage('Händelse borttagen') })
        .catch(err => { showMessage(JSON.stringify(err), 'error'); throw err })
    })
  }

  if (loading || !candidate) return <div className="CandidateDetail wide-margin"><SkeletonLoader /></div>

  const current = getMilestone(candidate.current_milestone_type)
  // The interview each milestone booked, so the timeline can show the slot and the edit
  // dialog can pre-fill it. Milestones migrated from the old interview_scheduled/
  // interviewed stages have no booking — the delta had nothing to build one from.
  const interviewByMilestoneId = new Map(interviews.filter(iv => iv.candidate_milestone_id != null)
    .map(iv => [iv.candidate_milestone_id, iv]))

  return (
    <div className="CandidateDetail wide-margin">

      <div className="cd-detail-header">
        <h1>
          {candidate.candidate_person_uuid
            ? <Link className="cd-person-link" to={`/network/persons/${candidate.candidate_person_uuid}`}>{candidate.candidate_name}</Link>
            : candidate.candidate_name}
        </h1>
        <div className="cd-detail-header-actions">
          <Button value="Redigera" className="button" onClick={editCandidate} />
          <Button value="+ Ny händelse" className="button main-button" onClick={() => showAddMilestoneDialog([candidate], addMilestone)} />
        </div>
      </div>

      <div className="cd-detail-layout">
        <div className="cd-contact-card">
          {candidate.candidate_email && (
            <div className="cd-contact-row"><span className="cd-contact-icon">E</span>{candidate.candidate_email}</div>
          )}
          {candidate.candidate_phone && (
            <div className="cd-contact-row"><span className="cd-contact-icon">T</span>{candidate.candidate_phone}</div>
          )}
          {candidate.candidate_linkedin && (
            <div className="cd-contact-row"><span className="cd-contact-icon">in</span>{candidate.candidate_linkedin}</div>
          )}
          {candidate.candidate_company_name && (
            <div className="cd-contact-row"><span className="cd-contact-icon">F</span>{candidate.candidate_company_name}</div>
          )}

          <div className="cd-detail-label">Ägare</div>
          <div>{candidate.owner_name}</div>

          {candidate.candidate_source && (
            <>
              <div className="cd-detail-label">Källa</div>
              <div>{candidate.candidate_source}</div>
            </>
          )}

          <div className="cd-detail-label">Nuvarande skede</div>
          <span className={current.pillClassName}>{current.label}</span>

          {flags.length > 0 && (
            <div className="cd-flags-prov">
              Flaggad via {flags.map((f, i) => (
                <span key={f.client_slug || f.client_name || i}>
                  {i > 0 && ' · '}
                  {f.client_name}{f.count > 1 && <span className="cd-flags-count"> ×{f.count}</span>}
                </span>
              ))}
            </div>
          )}

          {candidate.candidate_notes && (
            <>
              <div className="cd-detail-label">Anteckningar</div>
              <div style={{ whiteSpace: 'pre-wrap' }}>{candidate.candidate_notes}</div>
            </>
          )}

          {candidate.candidate_links && candidate.candidate_links.length > 0 && (
            <>
              <div className="cd-detail-label">Länkar</div>
              <div className="cd-links-row">
                {candidate.candidate_links.map((link, i) => (
                  <a key={i} className="cd-link-pill" href={link.url} target="_blank" rel="noreferrer">{link.label}</a>
                ))}
              </div>
            </>
          )}
        </div>

        <div className="cd-timeline-col">
          <div className="cd-detail-label">Historik</div>

          <div className="cd-timeline">
            {milestones.map(m => {
              const info = getMilestone(m.milestone_type)
              const interview = interviewByMilestoneId.get(m.candidate_milestone_id) || null
              return (
                <div className="cd-timeline-item" key={m.candidate_milestone_id}>
                  <span className={`cd-timeline-dot cd-timeline-dot-${info.value}`} />
                  <div className="cd-timeline-body">
                    <div className="cd-timeline-head">
                      <span className="cd-timeline-date">{new Date(m.milestone_date).toLocaleDateString('sv-SE')}</span>
                      <span className={info.pillClassName}>{info.label}</span>
                      <div className="cd-timeline-actions">
                        <div className="icon-edit" title="Redigera" onClick={() => editMilestone({ ...m, interview })} />
                        <div className="icon-delete" title="Ta bort" onClick={() => deleteMilestone(m)} />
                      </div>
                    </div>
                    {/* No emoji prefix here: 🗓 (U+1F5D3) has no glyph in the fonts this app
                        ships and rendered as a tofu box. The pill above already says which
                        round this is, so a plain "Bokad" label carries it. */}
                    {interview && (
                      <div className="cd-timeline-note">
                        <span className="cd-timeline-slot-label">Bokad</span>
                        {new Date(interview.interview_start_timestamp).toLocaleString('sv-SE', { dateStyle: 'short', timeStyle: 'short' })}
                        {interview.interview_location ? ` · ${interview.interview_location}` : ''}
                      </div>
                    )}
                    {m.milestone_note && <div className="cd-timeline-note">{m.milestone_note}</div>}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}
